import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Download, Share, PlusSquare, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

const detectPlatform = () => {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  const isIOS = /iPhone|iPad|iPod/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPadOS
  if (isIOS) {
    // Chrome / Firefox on iOS can't install PWAs — must be Safari
    const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
    return isSafari ? 'ios-safari' : 'ios-other';
  }
  if (/Android/i.test(ua)) return 'android';
  return 'other';
};

export const InstallCard = () => {
  const [installed, setInstalled] = useState(false);
  const [platform, setPlatform] = useState('other');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    setInstalled(isStandalone());
    setPlatform(detectPlatform());

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      toast.success('NEXA installed to your Home Screen');
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const onInstallClick = async () => {
    if (platform === 'ios-safari' || platform === 'ios-other') {
      setShowIOSGuide(true);
      return;
    }
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setInstalled(true);
      }
      setDeferredPrompt(null);
      return;
    }
    // Fallback — show generic instructions
    setShowIOSGuide(true);
  };

  if (installed) {
    return (
      <div
        className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-6"
        data-testid="nexa-install-card"
      >
        <div className="flex items-center gap-2 mb-3">
          <Check className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
          <span className="text-xs tracking-[0.2em] uppercase text-white/40">Installed</span>
        </div>
        <p className="text-sm text-white/60">
          NEXA is running from your Home Screen. You can now enable notifications below.
        </p>
      </div>
    );
  }

  const btnLabel =
    platform === 'ios-safari' || platform === 'ios-other'
      ? 'Add to Home Screen'
      : deferredPrompt
        ? 'Install NEXA'
        : 'Add to Home Screen';

  return (
    <>
      <div
        className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-6"
        data-testid="nexa-install-card"
      >
        <div className="flex items-center gap-2 mb-3">
          <Download className="w-4 h-4 text-sky-400" strokeWidth={1.5} />
          <span className="text-xs tracking-[0.2em] uppercase text-white/40">Install NEXA</span>
        </div>
        <div className="flex items-center gap-4 mb-4">
          <img
            src="/nexa-icon-192.png"
            alt="NEXA"
            className="w-14 h-14 rounded-2xl border border-white/10"
            data-testid="nexa-install-icon"
          />
          <p className="text-sm text-white/60 flex-1">
            Install NEXA on your Home Screen so it opens like a native app and can send you
            morning & evening notifications.
          </p>
        </div>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={onInstallClick}
          data-testid="nexa-install-button"
          className="w-full h-12 rounded-xl bg-white text-black font-semibold flex items-center justify-center gap-2"
        >
          <Download className="w-4 h-4" />
          {btnLabel}
        </motion.button>
        {platform === 'ios-other' && (
          <p className="mt-3 text-xs text-amber-400/80">
            On iPhone, install works only in Safari. Open <b>levanta.life/nexa</b> in Safari, then
            tap Add to Home Screen.
          </p>
        )}
      </div>

      <AnimatePresence>
        {showIOSGuide && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setShowIOSGuide(false)}
            data-testid="nexa-install-guide"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0A0A0A] p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <img
                    src="/nexa-icon-192.png"
                    alt="NEXA"
                    className="w-10 h-10 rounded-xl border border-white/10"
                  />
                  <div>
                    <div className="text-white text-base font-semibold">Install NEXA</div>
                    <div className="text-white/50 text-xs">iPhone · Safari</div>
                  </div>
                </div>
                <button
                  onClick={() => setShowIOSGuide(false)}
                  className="text-white/50 hover:text-white p-1"
                  data-testid="nexa-install-guide-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {platform === 'ios-other' && (
                <div className="mb-4 rounded-xl border border-amber-400/30 bg-amber-500/10 p-3 text-sm text-amber-200">
                  You&apos;re not using Safari. iOS can only install PWAs from Safari. Open
                  <b> levanta.life/nexa </b> in Safari, then follow the steps below.
                </div>
              )}

              <ol className="space-y-4 text-sm text-white/80">
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs shrink-0">1</span>
                  <span className="flex-1 flex items-center gap-2 flex-wrap">
                    Tap the
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 border border-white/10">
                      <Share className="w-4 h-4 text-sky-400" /> Share
                    </span>
                    button in the Safari toolbar.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs shrink-0">2</span>
                  <span className="flex-1 flex items-center gap-2 flex-wrap">
                    Scroll and tap
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 border border-white/10">
                      <PlusSquare className="w-4 h-4 text-sky-400" /> Add to Home Screen
                    </span>
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs shrink-0">3</span>
                  <span className="flex-1">
                    Confirm the name <b>NEXA</b> and tap <b>Add</b>. The NEXA icon will appear on
                    your Home Screen.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs shrink-0">4</span>
                  <span className="flex-1">
                    Open NEXA from the Home Screen icon, come back to Settings, and tap
                    <b> Enable notifications</b>.
                  </span>
                </li>
              </ol>

              <button
                onClick={() => setShowIOSGuide(false)}
                data-testid="nexa-install-guide-got-it"
                className="mt-6 w-full h-12 rounded-xl bg-white text-black font-semibold"
              >
                Got it
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
